import SelectionState from "./SelectionState";
import { createSelector } from "reselect";
import { EntityType, getEntityName } from "@ls/entity-service";
import { GroupTypeEntity } from "./GroupType";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { selectEntities } from "state/data/selectors";
import { memoize } from '@ls/js-utils';

export const selectSelectedAsOfDate = (selectionState) => (state) => state.selection[selectionState].asOfDate;
export const selectSelectedAsOfDateStart = (selectionState) => (state) => state.selection[selectionState].asOfDateStart;
export const selectSymbolicDate = (selectionState) => (state) => state.selection[selectionState].symbolicDate;
export const selectSymbolicDateStart = (selectionState) => (state) => state.selection[selectionState].symbolicDateStart;
export const selectSelectedMajorMinor = (selectionState) => (state) => state.selection[selectionState].majorMinor;
export const selectSelectedIsDisplayRgl = (selectionState) => (state) => state.selection[selectionState].isDisplayRgl;
export const selectSelectedIsShowCanceledTrades = (selectionState) => (state) => state.selection[selectionState].isShowCanceledTrades;
export const selectSelectedReplaceMajorsWithMinors = (selectionState) => (state) => state.selection[selectionState].replaceMajorsWithMinors;
export const selectSelectedGroupTypeValue = (selectionState) => (state) => state.selection[selectionState].groupType;
export const selectSelectedGroupValues = (selectionState) => (state) => state.selection[selectionState].groups;
export const selectModeValue = (selectionState) => (state) => state.selection[selectionState].mode;
export const selectSelectedFetchingGroupsByGroupTypeMap = (state) => state.selection.fetchingGroupsByGroupTypeMap;
export const selectSelectedGroupsByGroupTypeMap = (state) => state.selection.groupsByGroupTypeMap;
export const selectSelectedLocalCurrencyFiltervalues = (selectionState) => (state) => state.selection[selectionState].localCurrencyCodes;
export const selectSelectedOfferingMarketsValues = (selectionState) => (state) => state.selection[selectionState].offeringMarkets;
export const selectSelectedTransactionCodeFiltervalues = (selectionState) => (state) => state.selection[selectionState].transactionCodes;
export const selectSelectedTransactionEffectFiltervalues = (selectionState) => (state) => state.selection[selectionState].transactionEffects;
export const selectSelectedTickerFiltervalues = (selectionState) => (state) => state.selection[selectionState].tickers;
export const selectCovarianceOptions = (state) => state.selection.covarianceOptions;
export const selectSelectedSecurityIdsValues = (selectionState) => (state) => state.selection[selectionState].securityIds;
export const selectSelectedInstrumentTypesValues = (selectionState) => (state) => state.selection[selectionState].instrumentTypes;
export const selectSelectedInstrumentTypeIncludeOptionValue = (selectionState) => (state) => state.selection[selectionState].instrumentTypesIncludeOption;
export const selectSelectedMsaaOneValues = (selectionState) => (state) => state.selection[selectionState].absMsaaSectors;
export const selectSelectedMsaaBucketCodes = (selectionState) => (state) => state.selection[selectionState].msaaBucketCodes;
export const selectSelectedTraderIds = (selectionState) => (state) => state.selection[selectionState].traderIds;

export const selectFetchingGroups = createSelector(
  selectSelectedGroupTypeValue(SelectionState.UNCOMMITTED),
  selectSelectedFetchingGroupsByGroupTypeMap,
  (selectedGroupTypeValue, fetchingGroupsByGroupTypeMap) => {
    if (selectedGroupTypeValue) {
      return fetchingGroupsByGroupTypeMap.get(selectedGroupTypeValue) || false;
    }
    return false;
  }
);
export const selectFetchingEntities = (state) => state.selection.fetchingEntities;

export const selectAllEntities = createSelector(
  (state) => state.selection.entities,
  (entities) => entities?.map(entity => ({
    ...entity,
    label: getEntityName(entity),
  }))
    ?.sort((e1, e2) => {
      if (e1.type === EntityType.BMARK && e2.type === EntityType.BMARK) {
        if (e1.label < e2.label) return -1;
        if (e1.label > e2.label) return 1;
      }
      if (e1.type === EntityType.BMARK) return 1;
      if (e2.type === EntityType.BMARK) return -1;
      if (e1.label < e2.label) return -1;
      if (e1.label > e2.label) return 1;
      return 0;
    }) ?? []
);

export const selectFetchingSelectionSecurities = (state) => state.selection.fetchingSelectionSecurityAttributes;
export const selectAllSelectionSecurityAttributes = (state) => state.selection.selectionSecurityAttributes;
export const selectRemovedSelectionFiltersDialog = (state) => state.selection.removedSelectionFiltersDialog;

export const selectAllIn2users= (state) => state.selection.in2Users;
export const selectFetchingIn2Users = (state) => state.selection.fetchingIn2Users;

export const selectDistinctInstrumentTypes = createSelector(
  selectAllSelectionSecurityAttributes,
  securityAttributes => {
    const setOfinstrumentTypes = securityAttributes?.map(e => e?.instrumentType)
                                                    .reduce(
                                                      (previousSet, currentType) => previousSet.add(currentType),
                                                      new Set()
                                                    ) ?? new Set()
    var arrayOfinstrumentTypes = Array.from(setOfinstrumentTypes).filter(instrumentType => instrumentType != null).sort()
    return arrayOfinstrumentTypes.map(e => ({ 'instrumentType': e }))
  }
)

export const selectDistinctMsaaSectors = createSelector(
  selectAllSelectionSecurityAttributes,
  securityAttributes => {
    const setOfinstrumentTypes = securityAttributes?.map(e => e?.absMsaaSector)
                                                    .reduce(
                                                      (previousSet, currentType) => previousSet.add(currentType),
                                                      new Set()
                                                    ) ?? new Set()
    var arrayOfinstrumentTypes = Array.from(setOfinstrumentTypes).filter(instrumentType => instrumentType != null).sort()
    return arrayOfinstrumentTypes.map(e => ({ 'absMsaaSector': e }))
  }
)

export const selectDistinctMsaaBuckets = createSelector(
  selectAllSelectionSecurityAttributes,
  (securityAttributes) => {
    const bucketsByCode = (securityAttributes ?? []).reduce((bucketMap, securityAttribute) => {
      const msaaBucketCode = securityAttribute?.msaaBucketCode;
      if (msaaBucketCode == null || bucketMap.has(msaaBucketCode)) {
        return bucketMap;
      }

      bucketMap.set(msaaBucketCode, {
        msaaBucketCode,
        msaaBucketDescription: securityAttribute?.msaaBucketDescription ?? "",
      });
      return bucketMap;
    }, new Map());

    return Array.from(bucketsByCode.values()).sort((leftBucket, rightBucket) => {
      if (leftBucket.msaaBucketCode < rightBucket.msaaBucketCode) return -1;
      if (leftBucket.msaaBucketCode > rightBucket.msaaBucketCode) return 1;
      return 0;
    });
  }
)



//selectDistinctMsaaSector
export const selectFetchingHoldingAttibutes = (state) => state.selection.fetchingHoldingsAttributes;
export const selectAllHoldingAttributes = createSelector(
  (state) => state.selection.holdingAttributes,
  (holdingAttributes) => holdingAttributes?.map(({ name }) => ({ label: name, value: name }))
    ?.sort((e1, e2) => {
      if (e1.label < e2.label) return -1;
      if (e1.label > e2.label) return 1;
      return 0;
    }) ?? []
)

export const selectSelectedEntityIds = (selectionState) => state => state.selection[selectionState].entityIds;

export const selectAllEntitiesByEntityId = createSelector(
  selectAllEntities,
  entities => entities?.reduce((byEntityId, entity) => byEntityId.set(entity.entityId, entity), new Map()) ?? new Map()
);
export const selectSelectedEntities = memoize((selectionState) => createSelector(
  selectAllEntitiesByEntityId,
  selectSelectedEntityIds(selectionState),
  (entitiesByEntityId, entityIds) => entityIds?.map(entityId => entitiesByEntityId.get(entityId)) ?? []
));

export const selectUncommittedGroupTypeEntity = createSelector(
  selectSelectedGroupTypeValue(SelectionState.UNCOMMITTED),
  (selectedGroupTypeValue) => GroupTypeEntity[selectedGroupTypeValue]
);
export const selectCommittedGroupTypeEntity = createSelector(
  selectSelectedGroupTypeValue(SelectionState.COMMITTED),
  (selectedGroupTypeValue) => GroupTypeEntity[selectedGroupTypeValue]
);
export const selectUncommittedAllGroupEntities = createSelector(
  selectSelectedGroupTypeValue(SelectionState.UNCOMMITTED),
  selectSelectedGroupsByGroupTypeMap,
  (selectedGroupTypeValue, groupsByGroupTypeMap) =>
    groupsByGroupTypeMap.get(selectedGroupTypeValue) || []
);
export const selectCommittedAllGroupEntities = createSelector(
  selectSelectedGroupTypeValue(SelectionState.COMMITTED),
  selectSelectedGroupsByGroupTypeMap,
  (selectedGroupTypeValue, groupsByGroupTypeMap) =>
    groupsByGroupTypeMap.get(selectedGroupTypeValue) || []
);

export const useEntitySelectorOptions = () => {
  const entities = useSelector(selectEntities);
  return useMemo(() => {
    return entities
      .map((entity) => ({
        ...entity,
        label: getEntityName(entity),
      }))
      .sort((e1, e2) => {
        if (e1.type === EntityType.BMARK && e2.type === EntityType.BMARK) {
          if (e1.label < e2.label) return -1;
          if (e1.label > e2.label) return 1;
        }
        if (e1.type === EntityType.BMARK) return 1;
        if (e2.type === EntityType.BMARK) return -1;
        if (e1.label < e2.label) return -1;
        if (e1.label > e2.label) return 1;
        return 0;
      });
  }, [entities]);
};
